import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  ChevronDown,
  ArrowRight,
} from "lucide-react";
import { navLinks } from "@/lib/data";
import { cn } from "@/lib/utils";
import logoText from "@/assets/logo_text.png";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const pathname = location.pathname;
  const { t, i18n } = useTranslation();

  const getTranslatedLabel = (label: string) => {
    const key = label.toLowerCase().replace(' ', '_');
    return t(`nav.${key}`, { defaultValue: label });
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setActiveDropdown(null);
  }, [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 bg-navy-950/80 backdrop-blur-xl border-b border-white/[0.06] transition-all duration-500",
          isScrolled && "shadow-[0_4px_30px_rgba(0,0,0,0.3)]"
        )}
      >
        <nav className="section-container section-padding">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={logoText}
                alt="Naincode Logo"
                className="h-10 w-auto object-contain group-hover:brightness-110 transition-all duration-300"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() =>
                    link.children && setActiveDropdown(link.label)
                  }
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={link.href}
                    className={cn(
                      "flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200",
                      pathname === link.href || pathname.startsWith(link.href + "/")
                        ? "text-neon-400"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    )}
                  >
                    {getTranslatedLabel(link.label)}
                    {link.children && (
                      <ChevronDown className="w-3.5 h-3.5 ml-0.5" />
                    )}
                  </Link>

                  {/* Dropdown */}
                  <AnimatePresence>
                    {link.children && activeDropdown === link.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-80 p-2 bg-navy-900/95 backdrop-blur-xl border border-white/[0.08] rounded-2xl shadow-2xl"
                      >
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="flex flex-col gap-1 p-3 rounded-xl hover:bg-white/5 transition-colors group"
                          >
                            <span className="text-sm font-medium text-white group-hover:text-neon-400 transition-colors">
                              {child.label}
                            </span>
                            <span className="text-xs text-white/40 line-clamp-1">
                              {child.description}
                            </span>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <div className="flex items-center bg-white/5 rounded-lg p-1 border border-white/10">
                {['en', 'id', 'zh'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => i18n.changeLanguage(lang)}
                    className={cn(
                      "px-2.5 py-1.5 text-xs font-bold rounded-md uppercase transition-all duration-200",
                      i18n.language === lang
                        ? "bg-neon-500/20 text-neon-400 shadow-[0_0_10px_rgba(0,220,130,0.2)]"
                        : "text-white/50 hover:text-white"
                    )}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              <Link to="/konsultasi" className="btn-primary text-sm !py-2.5 !px-6">
                {t('nav.konsultasi_gratis')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Mobile Toggle */}
            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className="lg:hidden p-2 text-white/70 hover:text-white"
            >
              {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsMobileOpen(false)}
            />
            <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-navy-950 border-l border-white/[0.06] overflow-y-auto">
              <div className="pt-24 pb-8 px-6 space-y-2">
                {navLinks.map((link) => (
                  <div key={link.label}>
                    <Link
                      to={link.href}
                      className={cn(
                        "flex items-center justify-between px-4 py-3 text-base font-medium rounded-xl transition-colors",
                        pathname === link.href
                          ? "text-neon-400 bg-neon-500/10"
                          : "text-white/70 hover:text-white hover:bg-white/5"
                      )}
                      onClick={() => !link.children && setIsMobileOpen(false)}
                    >
                      {getTranslatedLabel(link.label)}
                      {link.children && (
                        <ChevronDown
                          className={cn(
                            "w-4 h-4 transition-transform",
                            activeDropdown === link.label && "rotate-180"
                          )}
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveDropdown(
                              activeDropdown === link.label ? null : link.label
                            );
                          }}
                        />
                      )}
                    </Link>
                    {link.children && activeDropdown === link.label && (
                      <div className="ml-4 mt-1 space-y-1">
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => setIsMobileOpen(false)}
                            className="block px-4 py-2.5 text-sm text-white/50 hover:text-neon-400 rounded-lg hover:bg-white/5 transition-colors"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div className="pt-4 mt-4 border-t border-white/[0.06] flex flex-col gap-4">
                  <div className="flex items-center justify-center bg-white/5 rounded-lg p-1 border border-white/10 mx-auto w-fit">
                    {['en', 'id', 'zh'].map((lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          i18n.changeLanguage(lang);
                        }}
                        className={cn(
                          "px-4 py-2 text-xs font-bold rounded-md uppercase transition-all duration-200",
                          i18n.language === lang
                            ? "bg-neon-500/20 text-neon-400 shadow-[0_0_10px_rgba(0,220,130,0.2)]"
                            : "text-white/50 hover:text-white"
                        )}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                  <Link
                    to="/konsultasi"
                    onClick={() => setIsMobileOpen(false)}
                    className="btn-primary w-full justify-center text-sm"
                  >
                    {t('nav.konsultasi_gratis')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
